/**
 * Nested cart helpers:
 * - sync child qty with parent
 * - centered loading on cart page groups
 * - remove hidden products when no visible items remain
 */
(() => {
  if (window.__customNestedCartInit) return;
  window.__customNestedCartInit = true;

  let clearingHiddenOnlyCart = false;

  const getParentKey = (item) => {
    if (!item?.parent_relationship) return null;
    return (
      item.parent_relationship.parent_key ||
      item.parent_relationship.parent?.key ||
      null
    );
  };

  const getHiddenProductIds = () =>
    (window.QG_HIDDEN_CART_PRODUCT_IDS || []).map((id) => Number(id)).filter(Boolean);

  const fetchCart = () =>
    fetch(`${theme.routes.cart_url}.js`, { credentials: 'same-origin' }).then((response) =>
      response.json()
    );

  const getSectionsToRequest = () => {
    const cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl?.getSectionsToRender) {
      return cartItemsEl
        .getSectionsToRender()
        .map((section) => section.section)
        .filter(Boolean);
    }

    const miniCart = document.querySelector('mini-cart');
    if (miniCart?.getSectionsToRender) {
      return miniCart
        .getSectionsToRender()
        .map((section) => section.section)
        .filter(Boolean);
    }

    return ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
  };

  const applyParsedSections = (parsedState) => {
    const cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl?.getSectionsToRender && cartItemsEl?.getSectionInnerHTML) {
      cartItemsEl.classList.toggle('is-empty', parsedState.item_count === 0);
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);

      cartItemsEl.getSectionsToRender().forEach((section) => {
        const element = document.getElementById(section.id);
        if (!element || !parsedState.sections?.[section.section]) return;
        const elementToReplace = element.querySelector(section.selector) || element;
        elementToReplace.innerHTML = cartItemsEl.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      });
      cartItemsEl.disableLoading?.();
      return;
    }

    const miniCart = document.querySelector('mini-cart');
    if (miniCart?.getSectionsToRender && miniCart?.getSectionInnerHTML) {
      miniCart.getSectionsToRender().forEach((section) => {
        const element = document.getElementById(section.id);
        if (!element || !parsedState.sections?.[section.section]) return;
        element.innerHTML = miniCart.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      });
    }
  };

  const removeHiddenProductsIfCartOnlyHidden = () => {
    if (clearingHiddenOnlyCart) return Promise.resolve(false);

    const hiddenIds = getHiddenProductIds();
    if (!hiddenIds.length) return Promise.resolve(false);

    return fetchCart().then((cart) => {
      const hasVisibleItem = (cart.items || []).some(
        (item) => !hiddenIds.includes(Number(item.product_id))
      );
      if (hasVisibleItem) return false;

      const hiddenItems = (cart.items || []).filter((item) =>
        hiddenIds.includes(Number(item.product_id))
      );
      if (!hiddenItems.length) return false;

      clearingHiddenOnlyCart = true;

      const updates = {};
      hiddenItems.forEach((item) => {
        updates[item.key] = 0;
      });

      const body = JSON.stringify({
        updates,
        sections: getSectionsToRequest(),
        sections_url: window.location.pathname,
      });

      return fetch(`${theme.routes.cart_update_url}`, {
        ...fetchConfig(),
        body,
      })
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);
          applyParsedSections(parsedState);

          document.dispatchEvent(
            new CustomEvent('cart:updated', {
              detail: { cart: parsedState, source: 'hidden-cart-cleanup' },
            })
          );
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hidden-cart-cleanup' });
          }
          return true;
        })
        .finally(() => {
          clearingHiddenOnlyCart = false;
        });
    });
  };

  const applySections = (cartItemsEl, parsedState, line, name) => {
    cartItemsEl.classList.toggle('is-empty', parsedState.item_count === 0);
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);

    if (parsedState.errors) {
      cartItemsEl.updateErrorLiveRegions(line, parsedState.errors);
    }

    cartItemsEl.getSectionsToRender().forEach((section) => {
      const element = document.getElementById(section.id);
      if (!element) return;

      const elementToReplace = element.querySelector(section.selector) || element;
      if (elementToReplace && parsedState.sections?.[section.section]) {
        elementToReplace.innerHTML = cartItemsEl.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      }
    });

    cartItemsEl.updateQuantityLiveRegions(line, parsedState.item_count);

    const lineItem = document.getElementById(`CartItem-${line}`);
    if (lineItem && name) {
      const focusTarget = lineItem.querySelector(`[name="${name}"]`);
      if (focusTarget) focusTarget.focus();
    }

    cartItemsEl.disableLoading();

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cart: parsedState },
      })
    );
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
    }
  };

  document.addEventListener('cart:updated', (event) => {
    if (event?.detail?.source === 'hidden-cart-cleanup') return;
    removeHiddenProductsIfCartOnlyHidden().catch((error) => {
      console.error('Hidden cart cleanup failed:', error);
    });
  });

  customElements.whenDefined('cart-items').then(() => {
    const CartItems = customElements.get('cart-items');
    if (!CartItems?.prototype?.updateQuantity) return;

    const originalGetSectionsToRender = CartItems.prototype.getSectionsToRender;
    if (originalGetSectionsToRender) {
      CartItems.prototype.getSectionsToRender = function getSectionsToRenderCustomCart() {
        return originalGetSectionsToRender.call(this).filter((section) => {
          if (section.id !== 'main-cart-footer') return true;
          const footer = document.getElementById('main-cart-footer');
          return !(footer && footer.classList.contains('custom-cart-footer-proxy'));
        });
      };
    }

    CartItems.prototype.enableLoading = function enableLoadingCentered(line) {
      const cartItems = document.getElementById('main-cart-items');
      if (cartItems) cartItems.classList.add('cart__items--disabled');

      this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));

      const lineKey = String(line);
      const lineItem =
        document.getElementById(`CartItem-${lineKey}`) ||
        this.querySelector(`[data-cart-line="${lineKey}"]`) ||
        this.querySelector(`[data-index="${lineKey}"]`)?.closest('li, .custom-cart-group, .product-container');

      const scope =
        lineItem?.closest?.('.custom-cart-group') ||
        lineItem?.closest?.('li') ||
        lineItem;

      const loadingOverlay =
        scope?.querySelector?.('.custom-cart-group__loading') ||
        scope?.querySelector?.('.loading-overlay');

      if (loadingOverlay) loadingOverlay.classList.remove('hidden');

      document.activeElement.blur();
      if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
    };

    const originalUpdateQuantity = CartItems.prototype.updateQuantity;

    CartItems.prototype.updateQuantity = function updateQuantityWithNestedSync(line, quantity, name) {
      const qty = parseInt(quantity, 10);

      if (Number.isNaN(qty) || qty === 0) {
        return originalUpdateQuantity.call(this, line, quantity, name);
      }

      this.enableLoading(line);

      fetchCart()
        .then((cart) => {
          const parentItem = cart.items[line - 1];
          if (!parentItem || getParentKey(parentItem)) {
            return originalUpdateQuantity.call(this, line, quantity, name);
          }

          const updates = {
            [parentItem.key]: qty,
          };

          let hasChildren = false;
          cart.items.forEach((item) => {
            if (getParentKey(item) === parentItem.key) {
              updates[item.key] = qty;
              hasChildren = true;
            }
          });

          if (!hasChildren) {
            return originalUpdateQuantity.call(this, line, quantity, name);
          }

          const sections = this.getSectionsToRender().map((section) => section.section);
          const body = JSON.stringify({
            updates,
            sections,
            sections_url: window.location.pathname,
          });

          return fetch(`${theme.routes.cart_update_url}`, {
            ...fetchConfig(),
            body,
          })
            .then((response) => response.text())
            .then((state) => {
              const parsedState = JSON.parse(state);
              applySections(this, parsedState, line, name);
            });
        })
        .catch(() => {
          this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
          this.disableLoading();
          if (this.cartErrors) {
            this.cartErrors.textContent = theme.cartStrings.error;
          }
        });
    };
  });
})();
